import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Grid, Link, Typography } from '@mui/material';
import {
	getPgClientAccessRequest,
	updatePgClientAccessRequest,
} from 'redux/reducers/pgSettings/reducer';
import {
	selectPgClientAccess,
	selectPgClientAccessLoading,
	selectPgSaveLoading,
} from 'redux/reducers/pgSettings/selectors';
import { TPgClientAccessArea } from 'services/api/paymentGateway/types';
import { PgPage } from '../common/PgPage';
import { PgPanel } from '../common/PgPanel';
import { EPgTab, PG_SETTINGS_GROUP, PG_TAB_LABELS, pgTabPath } from '../constants';
import { formatDateTime } from '../utils';
import { AccessAreaCard } from './AccessAreaCard';
import { CustomerEmailsPanel } from './CustomerEmailsPanel';
import { EasUpdatesPanel } from './EasUpdatesPanel';
import {
	ACCESS_AREA_KEYS,
	ACCESS_AREAS,
	isAreaChanged,
	isChanged,
	TAccessAreaKey,
	TClientAccessFormValues,
	toRequest,
	toValues,
	validate,
} from './form';

/**
 * Settings → Access & Notifications: what customers may do themselves (pay page, payment methods,
 * payments in the merchant dashboard), the pause of EAS invoice updates and the customer e-mails
 * of the gateway. One form, saved at once with `PUT /api/admin/client-access`.
 */
export const ClientAccess: FC = () => {
	const dispatch = useDispatch();
	const settings = useSelector(selectPgClientAccess);
	const loading = useSelector(selectPgClientAccessLoading);
	const saving = useSelector(selectPgSaveLoading('clientAccess'));
	const [values, setValues] = useState<TClientAccessFormValues | null>(null);

	useEffect(() => {
		dispatch(getPgClientAccessRequest());
	}, [dispatch]);

	// Every new settings object (loaded or returned by a save) resets the form.
	const [syncedFrom, setSyncedFrom] = useState<typeof settings | undefined>(undefined);
	if (settings !== syncedFrom) {
		setSyncedFrom(settings);
		setValues(settings ? toValues(settings) : null);
	}

	const errors = useMemo(() => (values ? validate(values) : {}), [values]);
	const hasErrors = Object.keys(errors).length > 0;
	const changed = useMemo(
		() => !!values && !!settings && isChanged(settings, values),
		[values, settings],
	);

	const setArea = (key: TAccessAreaKey, area: TPgClientAccessArea) =>
		setValues((v) => (v ? { ...v, [key]: area } : v));

	const setEasUpdatesPaused = (paused: boolean) =>
		setValues((v) => (v ? { ...v, easUpdatesPaused: paused } : v));

	const setNotification = (type: string, enabled: boolean) =>
		setValues((v) =>
			v
				? {
						...v,
						notifications: v.notifications.map((n) => (n.type === type ? { ...n, enabled } : n)),
					}
				: v,
		);

	const setAllNotifications = (enabled: boolean) =>
		setValues((v) =>
			v ? { ...v, notifications: v.notifications.map((n) => ({ ...n, enabled })) } : v,
		);

	const handleDiscard = () => {
		if (settings) setValues(toValues(settings));
	};

	const handleSave = () => {
		if (!values || !changed || hasErrors || saving) return;
		dispatch(updatePgClientAccessRequest(toRequest(values)));
	};

	// What customers face right now (saved settings, not the values being edited).
	const blockedNow = settings ? ACCESS_AREA_KEYS.filter((key) => !settings[key].enabled) : [];

	return (
		<PgPage
			title={PG_TAB_LABELS[EPgTab.CLIENT_ACCESS]}
			group={PG_SETTINGS_GROUP}
			actions={
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{(loading || saving) && <CircularProgress size={18} />}
					<Button size="small" onClick={handleDiscard} disabled={!changed || saving}>
						Discard changes
					</Button>
					<Button
						size="small"
						variant="contained"
						onClick={handleSave}
						disabled={!changed || hasErrors || saving}
					>
						Save
					</Button>
				</Box>
			}
		>
			{!values || !settings ? (
				<PgPanel>
					<Typography variant="body2" color="text.secondary">
						{loading ? 'Loading…' : 'No data.'}
					</Typography>
				</PgPanel>
			) : (
				<>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: 1,
							flexWrap: 'wrap',
							mb: 1,
						}}
					>
						<Typography variant="caption" color="text.secondary">
							{settings.updatedAt
								? `Last changed by ${settings.updatedBy ?? '—'} at ${formatDateTime(settings.updatedAt)}`
								: 'Not changed yet: default settings'}
						</Typography>
						{changed && (
							<Typography variant="caption" color="warning.main" sx={{ fontWeight: 'bold' }}>
								Unsaved changes{hasErrors ? ' — fix the errors to save' : ''}
							</Typography>
						)}
					</Box>
					{(blockedNow.length > 0 || settings.easUpdatesPaused) && (
						<Alert severity="warning" sx={{ mb: 2 }}>
							{blockedNow.length > 0 && (
								<div>
									Blocked for customers now:{' '}
									<b>{blockedNow.map((key) => ACCESS_AREAS[key].title).join(', ')}</b>.
								</div>
							)}
							{settings.easUpdatesPaused && (
								<div>
									EAS invoice updates are paused: <b>{settings.parkedEasUpdates}</b> queued.
								</div>
							)}
						</Alert>
					)}
					<PgPanel title="Customer access">
						<Grid container spacing={2}>
							{ACCESS_AREA_KEYS.map((key) => (
								<Grid item xs={12} md={6} xl={3} key={key}>
									<AccessAreaCard
										title={ACCESS_AREAS[key].title}
										description={ACCESS_AREAS[key].description}
										note={
											key === 'payments' ? (
												<Typography variant="body2" color="text.secondary">
													Automatic monthly charges are not controlled here: they are set in{' '}
													<Link component={RouterLink} to={pgTabPath(EPgTab.SCHEDULE)}>
														Settings → {PG_TAB_LABELS[EPgTab.SCHEDULE]}
													</Link>
													.
												</Typography>
											) : undefined
										}
										value={values[key]}
										changed={isAreaChanged(settings[key], values[key])}
										error={errors[key]}
										disabled={saving}
										onChange={(area) => setArea(key, area)}
									/>
								</Grid>
							))}
						</Grid>
					</PgPanel>
					<EasUpdatesPanel
						paused={values.easUpdatesPaused}
						savedPaused={settings.easUpdatesPaused}
						parkedEasUpdates={settings.parkedEasUpdates}
						disabled={saving}
						onChange={setEasUpdatesPaused}
					/>
					<CustomerEmailsPanel
						notifications={values.notifications}
						saved={settings.notifications}
						disabled={saving}
						onToggle={setNotification}
						onSetAll={setAllNotifications}
					/>
				</>
			)}
		</PgPage>
	);
};
